import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Activity, RefreshCw, CheckCircle2, XCircle, Loader2, Database, Cloud, HardDrive,
  Zap, KeyRound, Server, ShieldCheck, HelpCircle, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

function StatusPill({ ok, label }) {
  return (
    <Badge className={ok ? "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30"
                          : "bg-red-50 text-red-700 border-red-200"}>
      {ok ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
      {label}
    </Badge>
  );
}

// Fase 27 · Guía asistida en español para cada item en rojo del diagnóstico.
function FixGuide({ title, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 rounded-md border border-brand-emerald/30 bg-brand-emerald-soft/40" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-brand-emerald-dark hover:bg-brand-emerald-soft/70 rounded-md"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1"><HelpCircle className="h-3.5 w-3.5" /> {title}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 text-xs text-stone-700 leading-relaxed space-y-1">{children}</div>
      )}
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, testid }) {
  return (
    <div className="card-elevated p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 rounded-md items-center justify-center bg-brand-emerald-soft text-brand-emerald-dark">
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </span>
        <h3 className="font-serif-display text-xl text-brand-ink">{title}</h3>
      </div>
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}

function KV({ k, v, mono = false }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-stone-100 pb-1.5">
      <span className="text-stone-500 text-xs uppercase tracking-widest">{k}</span>
      <span className={`text-brand-ink ${mono ? "font-mono text-xs" : ""}`}>{v}</span>
    </div>
  );
}

export default function Diagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.get("/admin/diagnostics");
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al obtener diagnóstico");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
      </div>
    );
  }
  if (!data) return null;

  const envRows = Object.entries(data.envs_present || {});
  const integrations = data.integrations || {};
  const storage = data.storage || {};
  const scheduler = data.scheduler || {};
  const counters = data.counters || {};
  const tombstones = data.tombstones || {};

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="diagnostics-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <span className="inline-flex h-10 w-10 rounded-lg items-center justify-center bg-brand-emerald text-white">
              <Activity className="h-5 w-5" strokeWidth={1.8} />
            </span>
            Estado del Sistema
          </h1>
          <p className="text-sm text-stone-500 mt-2">
            Diagnóstico en vivo del backend, storage e integraciones. Útil para validar despliegues.
          </p>
          <p className="text-[11px] text-stone-400 mt-1">
            Última verificación: {new Date(data.checked_at).toLocaleString("es-ES")}
          </p>
        </div>
        <Button
          onClick={load}
          disabled={refreshing}
          data-testid="btn-refresh-diagnostics"
          className="bg-brand-emerald text-white hover:bg-brand-emerald-dark"
        >
          {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Volver a probar
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Ambiente" icon={Server} testid="diag-env">
          <KV k="APP_ENV" v={<Badge className={data.is_production ? "bg-brand-emerald text-white" : "bg-brand-gold text-brand-ink"}>{data.app_env || "(no set)"}</Badge>} />
          <KV k="Producción" v={<StatusPill ok={!!data.is_production} label={data.is_production ? "Sí" : "No (preview)"} />} />
          {!data.is_production && (
            <FixGuide title="¿Cómo lo corrijo?" testid="fix-app-env">
              1. Abre el panel Emergent → tu proyecto → <strong>Deployment</strong> → <strong>Environment Variables</strong> (backend).<br/>
              2. Agrega <code>APP_ENV=production</code> (o edítala si ya existe).<br/>
              3. Redeploy. Al terminar, refresca este diagnóstico y verifica que la etiqueta pase a verde.
            </FixGuide>
          )}
        </SectionCard>

        <SectionCard title="Almacenamiento" icon={storage.backend === "s3" ? Cloud : HardDrive} testid="diag-storage">
          <KV k="Backend" v={<Badge className={storage.backend === "s3" ? "bg-brand-emerald text-white" : "bg-red-50 text-red-700 border-red-200"}>{storage.backend?.toUpperCase() || "?"}</Badge>} />
          {storage.bucket && <KV k="Bucket" v={<span className="font-mono text-xs">{storage.bucket}</span>} />}
          {storage.public_base_url && <KV k="PUBLIC_BASE_URL" v={<span className="font-mono text-xs">{storage.public_base_url}</span>} />}
          <KV k="Prueba de subida" v={<StatusPill ok={!!storage.test_url} label={storage.test_url ? "OK" : "Falló"} />} />
          <KV k="URL absoluta" v={<StatusPill ok={!!storage.test_url_absolute} label={storage.test_url_absolute ? "Sí" : "No — Meta NO podrá fetch"} />} />
          <KV k="HEAD público" v={<StatusPill ok={!!storage.public_ok} label={storage.public_ok ? `HTTP ${storage.public_status}` : (storage.public_error || `HTTP ${storage.public_status || "?"}`)} />} />
          {storage.backend === "local" && (
            <>
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">
                <strong>⚠ Almacenamiento local:</strong> los uploads se perderán en cada redeploy y Meta no puede fetchear URLs relativas. Configure las env vars TIGRIS_* en el panel de deployment.
              </div>
              <FixGuide title="¿Cómo lo corrijo? (Tigris — recomendado)" testid="fix-tigris">
                1. En el panel Emergent → <strong>Deployment</strong> → <strong>Secrets</strong>, busca las credenciales de Tigris de tu proyecto.<br/>
                2. Copia y crea estas 4 env vars en el backend:<br/>
                <code className="block bg-stone-100 p-2 rounded my-1 text-[11px]">TIGRIS_ACCESS_KEY_ID=…<br/>TIGRIS_SECRET_ACCESS_KEY=…<br/>TIGRIS_ENDPOINT=https://…tigris.dev<br/>TIGRIS_BUCKET=nombre-del-bucket</code>
                3. Si el panel no muestra credenciales de Tigris, contacta a <strong>Soporte Emergent</strong> desde el chat del panel.<br/>
                4. Redeploy y refresca este diagnóstico. La etiqueta pasará a <strong>S3</strong> (verde) y las publicaciones IG dejarán de fallar.
              </FixGuide>
              {!storage.public_base_url && (
                <FixGuide title="Alternativa temporal: PUBLIC_BASE_URL" testid="fix-public-base">
                  Si NO puedes activar Tigris ahora, configura al menos <code>PUBLIC_BASE_URL=https://7-study.com</code> en las env vars del backend. Con esto, las URLs relativas del storage local se absolutizan y algunas publicaciones podrán funcionar (aunque los uploads se pierden en cada redeploy).
                </FixGuide>
              )}
            </>
          )}
        </SectionCard>

        <SectionCard title="Integraciones" icon={KeyRound} testid="diag-integrations">
          {Object.entries(integrations).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between">
              <span className="text-stone-600 capitalize">{k}</span>
              <StatusPill ok={!!v} label={v ? "Configurado" : "No configurado"} />
            </div>
          ))}
          {Object.entries(integrations).some(([_, v]) => !v) && (
            <FixGuide title="¿Cómo configuro las integraciones?" testid="fix-integrations">
              Ve a <strong>Configuración Maestra → Integraciones</strong> en el sidebar. Cada tarjeta lista los campos requeridos y dónde obtenerlos:<br/>
              • <strong>Composio</strong>: crea una API key en <a href="https://dashboard.composio.dev" target="_blank" rel="noreferrer" className="text-brand-emerald-dark underline">dashboard.composio.dev</a> → sección <em>API keys</em>.<br/>
              • <strong>WhatsApp Cloud</strong>: en Meta Business → WhatsApp → <em>API Setup</em> obtén el token permanente y el <em>phone_number_id</em>. (NO existe QR — sólo Cloud API oficial.)<br/>
              • <strong>Resend</strong> (email): crea una API key en <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-brand-emerald-dark underline">resend.com</a>.<br/>
              • <strong>MercadoLibre</strong>: sigue el OAuth guiado desde su tarjeta.
            </FixGuide>
          )}
        </SectionCard>

        <SectionCard title="Scheduler" icon={Zap} testid="diag-scheduler">
          <KV k="Intervalo" v={`${scheduler.interval_sec}s`} />
          <KV k="Procesadores" v={scheduler.processors_registered} />
          <KV k="Corridas" v={scheduler.runs_completed} />
          <KV k="Última corrida" v={scheduler.last_run_at ? new Date(scheduler.last_run_at).toLocaleString("es-ES") : "—"} />
          {scheduler.last_error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-1">
              <strong>Último error:</strong> {scheduler.last_error}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Variables de entorno" icon={ShieldCheck} testid="diag-envs">
          <div className="text-xs text-stone-500 mb-1">Presencia (sin valores).</div>
          <div className="grid grid-cols-1 gap-1">
            {envRows.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <code className="text-xs text-stone-700">{k}</code>
                <StatusPill ok={!!v} label={v ? "Presente" : "Ausente"} />
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Base de datos" icon={Database} testid="diag-counters">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(counters).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm border-b border-stone-100 pb-1">
                <span className="text-stone-500 text-xs">{k}</span>
                <span className="font-mono font-semibold text-brand-ink">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-stone-100 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-stone-600 text-xs">Tombstone demo_purged</span>
              <StatusPill ok={!!tombstones.demo_purged} label={tombstones.demo_purged ? "Activo" : "No"} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone-600 text-xs">Tombstone reset_to_zero</span>
              <StatusPill ok={!!tombstones.reset_to_zero} label={tombstones.reset_to_zero ? "Activo" : "No"} />
            </div>
          </div>
        </SectionCard>
      </div>

      {data.recent_scheduler_events?.length > 0 && (
        <div className="card-elevated p-5">
          <h3 className="font-serif-display text-xl text-brand-ink mb-3">Últimos eventos del scheduler</h3>
          <div className="space-y-2 text-sm">
            {data.recent_scheduler_events.map((e, i) => (
              <div key={i} className="flex items-start gap-3 border-b border-stone-100 pb-2">
                <Badge className={e.action.includes("failed") ? "bg-red-50 text-red-700 border-red-200" : "bg-brand-emerald-soft text-brand-emerald-dark"}>
                  {e.action.includes("failed") ? "Falló" : "OK"}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-brand-ink text-xs truncate">{e.detail}</div>
                  <div className="text-[10px] text-stone-400">{new Date(e.timestamp).toLocaleString("es-ES")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
